import { filtersBarFilters } from '@/components/UI/organisms/FiltersBar/filters.fixtures';
import { postedLabel } from './posted';
import { sidebarSections } from '@/components/UI/organisms/Sidebar/sidebar.fixtures';
import { USER_BRAND } from './brand';
import { AI_CREDITS_AT_START, AI_CREDITS_TOTAL } from './credits';
import { mulberry32 } from './prng';
import { REFERENCE_DATE } from './reference';

/**
 * Everything the four Settings tabs draw — page 825:18114.
 *
 * **This module is a fixture, not a query.** Settings is inert by decision
 * (2026-09-21): it renders what the frames draw and writes nothing anybody else
 * reads. No corpus row is touched here, no chip is re-scoped, and `Save
 * changes` commits to nothing. Every other `data/*` module in this folder
 * answers a question about the corpus; this one answers "what is on the
 * screen", which is why it holds literals where its neighbours hold reducers.
 *
 * The one thing it does borrow is the **option lists**, read out of the filter
 * chip fixtures rather than retyped — `data/dimensions.ts`' argument, for its
 * reason: a second hand-written copy of "what channels exist" is the drift that
 * file was written to prevent. Borrowing the list is not the same as driving
 * it; nothing here writes back.
 */

export type SettingsTab =
  | 'account'
  | 'configurations'
  | 'sentiment-tuning'
  | 'team-permissions'
  | 'usage';

/**
 * The bar the four frames draw, in their order. `account` is the default and
 * the only one with no path segment of its own — `#settings` is Account, the
 * way `#custom-reports` is the list of reports.
 */
export const settingsTabs: readonly { value: SettingsTab; label: string }[] = [
  { value: 'account', label: 'Account' },
  { value: 'configurations', label: 'Configurations' },
  { value: 'sentiment-tuning', label: 'Sentiment tuning' },
  { value: 'team-permissions', label: 'Team permissions' },
  /*
   * Added 2026-09-21 with `Settings - (AI credit usage)` (2135:63277). The
   * frame names it for what it measures; the tab itself just reads `Usage`.
   */
  { value: 'usage', label: 'Usage' },
];

const tabValues = new Set<string>(settingsTabs.map((tab) => tab.value));

export function isSettingsTab(value: string): value is SettingsTab {
  return tabValues.has(value);
}

/**
 * The tab a URL segment names, falling back to Account.
 *
 * The fallback is belt-and-braces rather than the guard: `isResolvable` already
 * drops a segment that is not one of the four, so `App` never passes one. It
 * exists so the view cannot be handed `undefined` by a future caller that
 * forgets the router does that.
 */
export function settingsTabFor(segment: string | undefined): SettingsTab {
  return segment !== undefined && isSettingsTab(segment) ? segment : 'account';
}

/* ------------------------------------------------------------------ Account */

/** 825:18115 — the three fields the Account frame fills in. */
export const account = {
  name: 'Grameenphone',
  /*
   * Figma draws it in `text/disabled` on `bg/primary` where the other fields
   * are `text/primary` on white — the field's `State=Disabled`, not a
   * placeholder. Read as "your sign-in address is not editable here", which is
   * also why it is the one Account field with no `Change` affordance.
   */
  email: 'grameen@deepdive.ai',
  timezone: 'utc',
} as const;

export type TimezoneOption = { value: string; label: string; offset: string };

/**
 * Figma fills one row — `Coordinated Universal Time (UTC)` with a dimmer
 * `UTC±00:00` after it, which is `Select`'s `supportingText`. The rest are
 * added so the control opens onto something; Dhaka leads them because the
 * corpus is a Bangladeshi operator's.
 */
export const timezones: readonly TimezoneOption[] = [
  { value: 'utc', label: 'Coordinated Universal Time (UTC)', offset: 'UTC±00:00' },
  { value: 'dhaka', label: 'Asia/Dhaka', offset: 'UTC+06:00' },
  { value: 'kolkata', label: 'Asia/Kolkata', offset: 'UTC+05:30' },
  { value: 'dubai', label: 'Asia/Dubai', offset: 'UTC+04:00' },
  { value: 'london', label: 'Europe/London', offset: 'UTC+01:00' },
  { value: 'singapore', label: 'Asia/Singapore', offset: 'UTC+08:00' },
  { value: 'new-york', label: 'America/New_York', offset: 'UTC−04:00' },
];

/* ----------------------------------------------------------- Configurations */

const optionsFor = (filterId: string) => {
  const filter = filtersBarFilters.find((candidate) => candidate.id === filterId);
  if (!filter) {
    /*
     * Loud rather than an empty list, for `data/dimensions.ts`' reason: a
     * silently empty multi-select reads as "nothing is being monitored", which
     * is a different and much worse statement than "this lookup is broken".
     */
    throw new Error(`settings: no filter chip with id "${filterId}".`);
  }
  return filter.options;
};

export const channelOptions = optionsFor('channels');
export const topicOptions = optionsFor('topics');

/**
 * Every brand the chip knows, minus our own — which is what a *competitor*
 * brand is, and which is why this is derived rather than listed. `USER_BRAND`
 * is the same constant the rest of the app scopes by, so the day Grameenphone
 * stops being the user's brand this list follows it.
 *
 * The frame draws two tags, `Robi` and `Banglalink`, and draws the same two in
 * the Keywords and Topics fields below it. Two of those three are wrong — a
 * competitor's name is not a topic — so the tags here come from the fixtures
 * the app actually uses. Flagged in plan.md §7; see the note on `keywords`.
 */
export const competitorBrands: readonly string[] = optionsFor('brands')
  .filter((option) => option.value !== USER_BRAND)
  .map((option) => option.label);

/**
 * What the corpus is monitored for. `data/keywords.ts` searches rows for a
 * query and has no canonical list to offer, so these are the terms the Trends
 * and Mentions cards actually surface, written down once here.
 */
export const keywords: readonly string[] = [
  '#Grameenphone',
  '#GP',
  '#network',
  '#5g',
  '#offer',
  'data pack',
];

/** The first six chip topics, which is what a monitoring list would name. */
export const topics: readonly string[] = topicOptions.slice(0, 6).map((option) => option.label);

/**
 * Figma draws `5 selected` on both multi-selects, so five of each is selected.
 * Channels takes the first five of the chip's seven; the languages are listed
 * below because the corpus has no language dimension at all.
 */
export const selectedChannels: readonly string[] = channelOptions
  .slice(0, 5)
  .map((option) => option.value);

/**
 * **No counterpart in the corpus.** A row carries brand, channel, intent,
 * topic, conversation and sentiment — see `data/dimensions.ts` — and nothing
 * anywhere records what language a mention is in. The control is drawn because
 * the frame draws it; the list is the one a Bangladeshi operator would monitor.
 * Flagged in plan.md §7.
 */
export const languages: { value: string; label: string }[] = [
  { value: 'bn', label: 'Bangla' },
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'ur', label: 'Urdu' },
  { value: 'ar', label: 'Arabic' },
  { value: 'ne', label: 'Nepali' },
];

export const selectedLanguages: readonly string[] = languages.slice(0, 5).map((l) => l.value);

/**
 * The two Slider handles — the negative/neutral and neutral/positive
 * boundaries — as the frame positions them (2098:6535).
 *
 * **25/75, where `Slider`'s own default is 33/66.** The component opens on even
 * thirds because that gives the neutral band the same weight as each pole; the
 * frame narrows it, which says a mention has to be more clearly positive or
 * negative before it is called either. The frame wins, because it is the
 * product's setting rather than the control's.
 *
 * Nothing reads this. A row's `sentiment` is a categorical label the generator
 * draws from a distribution, not a 0-100 score, so there is no number for a
 * threshold to cut — see the note in `SentimentThreshold`.
 */
export const sentimentThreshold: readonly [number, number] = [25, 75];

/* --------------------------------------------------------- Sentiment tuning */

export type TuningLogEntry = {
  id: string;
  /** The post the correction was made on. */
  summary: string;
  /** What the classifier said. */
  previous: 'positive' | 'neutral' | 'negative';
  /** What a person changed it to. */
  current: 'positive' | 'neutral' | 'negative';
};

/**
 * 2092:52451 — five corrections, in the frame's order and with the frame's
 * sentiment pairs.
 *
 * **The summaries are not the frame's.** It fills the `Post summary` column
 * with `John Doe(You) / Jimmy / Doe / Sarah / Bob` — the five names from the
 * Team members table one frame over, pasted across. A person's name is not a
 * post summary and the column header says what the column holds, so these are
 * post summaries. The pairs, the count and the order are untouched. Flagged in
 * plan.md §7.
 *
 * Deliberately **not** read from `state.sentimentOverrides`, the store Tune
 * sentiment writes to. That wiring was offered and declined (2026-09-21): this
 * tab shows a fixed log, and a correction made on Competitors does not appear
 * here. The store is where it would come from — see the note in
 * `SentimentTuningTab`.
 */
export const tuningLog: readonly TuningLogEntry[] = [
  {
    id: 'log-1',
    summary: 'Finally got 5G at Banani — took three months but worth the wait',
    previous: 'positive',
    current: 'negative',
  },
  {
    id: 'log-2',
    summary: 'Great, another “network upgrade” that knocked out my data all morning',
    previous: 'positive',
    current: 'negative',
  },
  {
    id: 'log-3',
    summary: 'Recharged through the app, took about a minute',
    previous: 'neutral',
    current: 'positive',
  },
  {
    id: 'log-4',
    summary: 'Love how my “unlimited” pack stops working at 20GB',
    previous: 'positive',
    current: 'negative',
  },
  {
    id: 'log-5',
    summary: 'Call dropped twice on the Dhaka–Chattogram highway',
    previous: 'negative',
    current: 'neutral',
  },
];

/* --------------------------------------------------------- Team permissions */

export type TeamRole = 'super-admin' | 'admin' | 'user';
export type TeamStatus = 'active' | 'pending';

export type TeamMember = {
  id: string;
  name: string;
  role: TeamRole;
  status: TeamStatus;
  /**
   * The signed-in person. Figma names the first row `John Doe(You)` and draws
   * its `Change role` in `text/disabled` — you cannot demote or remove
   * yourself, which is the rule the greyed row states.
   */
  self?: boolean;
  /**
   * The modules this member can open — `ModuleId`s. Admins and Users only; a
   * Super admin sees every module, so has none recorded. See `roleHasModules`.
   */
  modules?: readonly ModuleId[];
};

/* ------------------------------------------------------- module access */

/**
 * What `Modules access` (2161:35620, added to the invite dialog 2026-09-22)
 * offers: every module in the sidebar **except Custom reports**, which has a
 * permission system of its own — a report is shared per person, so gating the
 * module as a whole would mean nothing (the user's note, 2026-09-22).
 *
 * Read off `sidebarSections` rather than listed, so a module added to or
 * removed from the sidebar is added to or removed from this list with it. The
 * footer's Settings is not in the sections and is not a module anyone is
 * granted: it is the workspace's own configuration.
 */
const MODULES_WITH_OWN_PERMISSIONS = new Set(['custom-reports']);

export const moduleOptions: readonly { value: string; label: string }[] = sidebarSections
  .flatMap((section) => section.items)
  .filter((item) => !MODULES_WITH_OWN_PERMISSIONS.has(item.id))
  .map((item) => ({ value: item.id, label: item.label }));

export type ModuleId = string;

/**
 * Whether a role's access is chosen module by module. Admin and User both are
 * (the user's call, 2026-09-22); a Super admin sees everything, so the field is
 * not shown for one.
 */
export function roleHasModules(role: string): boolean {
  return role === 'admin' || role === 'user';
}

/*
 * The existing members' access. The frame records none — the table has no
 * modules column — so these exist to give `Change role` something true to open
 * on: an Admin moved to User keeps the modules they already had, and changing
 * them is a choice rather than a blank form.
 */
const MONITORING = ['overview', 'mentions', 'influencers', 'media', 'brand-health', 'trends'];
const EVERYTHING = moduleOptions.map((option) => option.value);

/** 2086:44153 — the five rows, their roles and their statuses, verbatim. */
export const teamMembers: readonly TeamMember[] = [
  { id: 'john-doe', name: 'John Doe', role: 'super-admin', status: 'active', self: true },
  { id: 'jimmy', name: 'Jimmy', role: 'admin', status: 'pending', modules: EVERYTHING },
  { id: 'doe', name: 'Doe', role: 'user', status: 'pending', modules: MONITORING },
  { id: 'sarah', name: 'Sarah', role: 'admin', status: 'active', modules: EVERYTHING },
  {
    id: 'bob',
    name: 'Bob',
    role: 'admin',
    status: 'active',
    modules: EVERYTHING.filter((id) => id !== 'deepdive-ai'),
  },
];

/*
 * The descriptions under each role in the select. Admin and User used to read
 * "everything" and "every module"; since module access, both are scoped to the
 * modules chosen for them, and saying otherwise would contradict the field
 * directly below.
 */
export const roles: readonly { value: TeamRole; label: string; supportingText: string }[] = [
  {
    value: 'super-admin',
    label: 'Super admin',
    supportingText: 'Every module, plus billing and team',
  },
  { value: 'admin', label: 'Admin', supportingText: 'Manages the modules you choose' },
  { value: 'user', label: 'User', supportingText: 'Views the modules you choose' },
];

const roleLabels = new Map(roles.map((role) => [role.value, role.label]));

export function roleLabel(role: TeamRole): string {
  return roleLabels.get(role) ?? role;
}

/** The signed-in person — the row whose `Change role` is disabled. */
export const signedInMember: TeamMember | undefined = teamMembers.find((member) => member.self);

/**
 * "John Doe" → "JD". The profile photo's placeholder on Settings → Account
 * (2147:69324) and the sidebar's profile header both draw these, so both call
 * this rather than each keeping a copy that could drift.
 */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join('');
}

/** How a member's name reads in the table — the frame's `John Doe(You)`. */
export function memberLabel(member: TeamMember): string {
  return member.self ? `${member.name}(You)` : member.name;
}

/* -------------------------------------------------------------------- Usage */

/*
 * Settings → Usage (2135:63277). Two things share one story here: the `Log`
 * table and the `AI credit usage tracker` card, and both have to agree with
 * the sidebar's `AI credits` meter (`data/credits.ts`), which is live.
 *
 * The rules that make them agree, all settled with the user on 2026-09-21:
 *
 * - **One credit per DeepDive answer** — the sidebar's rule, not this file's.
 * - **A Log row is a chat**, and its `credits` is how many answers that chat
 *   generated (the opening question, follow-ups and Regenerates). That keeps
 *   the frame's 5 / 12 / 4 / 7 / 9 and the one-credit rule both true.
 * - **The 2,671 allowance is a 30-day cycle.** The sidebar opens on 2,137 left,
 *   so 534 were spent before the session; the 30-day history below adds up to
 *   exactly that. Anything spent in the session (in `ChatContext`) is stamped
 *   "now" and added on top, so used + left is always the allowance.
 * - **"Now" is `REFERENCE_DATE`** — Tue Sep 15, 00:00, the instant DeepDive's
 *   chat times are measured from. Every range therefore ends at midnight: 1d is
 *   the last 24 hours of Sep 14, 7d is Sep 8–14, 30d is Aug 16 – Sep 14.
 */

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const NOW = REFERENCE_DATE.getTime();

export const CREDITS_SPENT_BEFORE_SESSION = AI_CREDITS_TOTAL - AI_CREDITS_AT_START;

export type UsageLogEntry = {
  id: string;
  /** When the chat was started. */
  at: Date;
  /** The chat's opening question. */
  query: string;
  /** Answers the chat generated — one credit each. */
  credits: number;
  /** A `TeamMember.id`. */
  memberId: string;
};

/**
 * `Log` (2135:63556) — five chats, one page.
 *
 * **The credits are the frame's**, in its order; a row is a chat and the figure
 * is its answer count (see above). Three columns are not the frame's:
 *
 * - **Query** — DeepDive questions (the user's call), where the frame has social
 *   posts from other brands.
 * - **Date** — the frame repeats `Mon sep 21, 1:25 PM`, after the prototype's
 *   "now". These fall in the last three days before it.
 * - **User** — Neil and Claire are not on the team and Jimmy is Pending, so the
 *   rows go to the three active members.
 *
 * Every row sits inside the 30-day history and its credits are part of the
 * bars — the log is five of the chats the chart counts, not a second source.
 * They are workspace chats, so John Doe's two are not the ones his own Chat
 * history seeds (`data/chats.ts`); that list is his and this is everybody's.
 */
const at = (day: number, hour: number, minute: number) => new Date(2026, 8, day, hour, minute);

export const usageLog: readonly UsageLogEntry[] = [
  {
    id: 'usage-1',
    at: at(14, 21, 42),
    query: 'Why did negative sentiment about network coverage spike on Monday?',
    credits: 5,
    memberId: 'john-doe',
  },
  {
    id: 'usage-2',
    at: at(14, 16, 8),
    query: 'Compare our share of voice with Robi and Banglalink over the last 30 days',
    credits: 12,
    memberId: 'sarah',
  },
  {
    id: 'usage-3',
    at: at(14, 11, 25),
    query: 'Which influencers mentioned our data packs this week?',
    credits: 4,
    memberId: 'bob',
  },
  {
    id: 'usage-4',
    at: at(13, 15, 3),
    query: 'Summarise the top complaints on Facebook since Friday',
    credits: 7,
    memberId: 'john-doe',
  },
  {
    id: 'usage-5',
    at: at(12, 10, 51),
    query: 'What are people saying about the new 5G rollout in Dhaka?',
    credits: 9,
    memberId: 'sarah',
  },
];

/**
 * The frame's `Mon sep 21, 1:25 PM`, with the month capitalised.
 *
 * Delegated to `postedLabel` since 2026-09-23, when the same format was asked
 * for on every table cell holding a specific moment. This frame is where it came
 * from, so the shared one is this one — it just has more callers now.
 */
export const usageDateLabel = (date: Date): string => postedLabel(date.getTime());

/* ------------------------------------------------ the 30-day history */

/**
 * Split `total` into integers proportional to `weights`, each at least its
 * `floor`, summing to `total` exactly — largest remainder, so no figure is off
 * by the rounding of the others.
 */
function allocate(total: number, weights: readonly number[], floors: readonly number[]): number[] {
  const spare = total - floors.reduce((a, b) => a + b, 0);
  if (spare < 0) throw new Error('settings: the usage floors exceed the total.');
  const sum = weights.reduce((a, b) => a + b, 0);
  const exact = weights.map((w) => (w / sum) * spare);
  const out = exact.map(Math.floor);
  let left = spare - out.reduce((a, b) => a + b, 0);
  const order = exact.map((x, i) => [x - Math.floor(x), i] as const).sort((a, b) => b[0] - a[0]);
  for (const [, i] of order) {
    if (left === 0) break;
    out[i] += 1;
    left -= 1;
  }
  return out.map((n, i) => n + floors[i]);
}

/** Midnight of day `i` of the window, 0 = Aug 16 … 29 = Sep 14. */
const dayStart = (i: number) => new Date(NOW - (30 - i) * DAY);

/** The credits each log row puts on its own day and hour. */
function logCredits(from: number, to: number, bucket: number): number[] {
  const out = new Array(Math.round((to - from) / bucket)).fill(0);
  for (const row of usageLog) {
    const t = row.at.getTime();
    if (t >= from && t < to) out[Math.floor((t - from) / bucket)] += row.credits;
  }
  return out;
}

/*
 * Weekdays busier than weekends, with seeded noise so the bars are not a comb.
 * Seeded rather than random so every reload draws the same 30 days — the
 * determinism rule the corpus keeps (`data/reference.ts`).
 */
const dailyCredits: readonly number[] = (() => {
  const rand = mulberry32(2671);
  const weights = Array.from({ length: 30 }, (_, i) => {
    const weekday = dayStart(i).getDay();
    const base = weekday === 0 || weekday === 6 ? 0.45 : 1;
    return base * (0.6 + rand() * 0.8);
  });
  const floors = logCredits(dayStart(0).getTime(), NOW, DAY);
  return allocate(CREDITS_SPENT_BEFORE_SESSION, weights, floors);
})();

/*
 * The last day split into hours: each log row lands in its own hour, and the
 * rest of the day's credits follow working hours (busiest 9–18, a trickle
 * after dark).
 */
const hourlyCredits: readonly number[] = (() => {
  const rand = mulberry32(534);
  const weights = Array.from({ length: 24 }, (_, h) => {
    const base = h >= 9 && h < 18 ? 1 : h >= 7 && h < 22 ? 0.4 : 0.05;
    return base * (0.5 + rand());
  });
  const floors = logCredits(NOW - DAY, NOW, HOUR);
  return allocate(dailyCredits[29], weights, floors);
})();

export type UsageRange = '1d' | '7d' | '30d';

export const usageRanges: readonly { value: UsageRange; label: string; phrase: string }[] = [
  { value: '1d', label: '1d', phrase: 'in the last 24 hours' },
  { value: '7d', label: '7d', phrase: 'in the last 7 days' },
  { value: '30d', label: '30d', phrase: 'in the last 30 days' },
];

const dayLabel = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const hourLabel = new Intl.DateTimeFormat('en-US', { hour: 'numeric' });

/**
 * The chart's bars for a range, oldest first, with `sessionCredits` — spent
 * since the page loaded, stamped "now" — added to the newest bar. It happened
 * at the very end of the window, which is the newest bar's edge, and leaving it
 * out would let the bars stop adding up to what the KPI and the sidebar say.
 */
export function usageSeries(
  range: UsageRange,
  sessionCredits: number,
): { label: string; credits: number }[] {
  const rows =
    range === '1d'
      ? hourlyCredits.map((credits, h) => ({
          label: hourLabel.format(new Date(NOW - DAY + h * HOUR)),
          credits,
        }))
      : dailyCredits
          .slice(range === '7d' ? -7 : 0)
          .map((credits, i, days) => ({
            label: dayLabel.format(dayStart(30 - days.length + i)),
            credits,
          }));
  rows[rows.length - 1] = {
    ...rows[rows.length - 1],
    credits: rows[rows.length - 1].credits + sessionCredits,
  };
  return rows;
}
